import React, { useCallback, useMemo, useState } from 'react';
import axios, { AxiosError } from 'axios';
import { clamp } from 'lodash';

import { CourseEntity, StudyPlanEntity } from 'Models/Entities';

import alertToast from 'Util/ToastifyUtils';
import {
	indexByPropertyArray, indexByPropertyMutate,
} from 'Util/DataIndexUtils';
import { safeAdd, safeSubtract } from 'Util/MathUtils';

import { useMounted, useOnMount } from 'Hooks/useMounted';
import useDebounce from 'Hooks/useDebounce';
import { abortedRequestedMessage, AbortSignals } from 'Hooks/useAbortSignals';
import { useStudyLoadConfig } from 'Queries/Hooks/StudyPlan/useStudyLoadConfig';
import { generatePlan } from 'Queries/REST/StudyPlan/StudyPlanRequests';
import {
	IGeneratedPlan, StudyLoadConfig, TermIndex, YearHalf,
} from 'Queries/Hooks/StudyPlan/types';
import { generatePeriodDefaultLoad, populateStudyLoad } from 'Queries/Hooks/StudyPlan/studyLoadHelpers';

export const handlePlanGenerationError = (err: Error | AxiosError) => {
	if (err.message === abortedRequestedMessage) {
		return;
	}

	if (axios.isAxiosError(err)) {
		alertToast(err.response?.data, 'error');
	} else {
		alertToast('Failed to generate a new plan', 'error');
	}
};

const useStudyPlan = (studyPlanId?: string) => {
	const mounted = useMounted();

	const [currentPlan, setCurrentPlan] = useState<IGeneratedPlan>();
	const [currentStudyLoad, setCurrentStudyLoad] = useState<StudyLoadConfig>([]);

	const generatedPlanFeedback = currentPlan?.feedback;

	// load study plan's program version study load config
	const retrievedStudyLoadConfig = useStudyLoadConfig(studyPlanId);

	const periodConfig = retrievedStudyLoadConfig.data?.studyPlanEntity.programVersion.studyPeriodConfigs;
	const termStudyLoadConfig = retrievedStudyLoadConfig.data?.studyPlanEntity.programVersion.termConfigs;

	const allStudyPeriods = useMemo(
		() => {
			const periods = periodConfig?.map(studyPeriodConfig => studyPeriodConfig.studyPeriod);
			periods?.sort((periodA, periodB) => periodA.order - periodB.order);
			return periods;
		},
		[periodConfig],
	);
	const allTerms = useMemo(
		() => termStudyLoadConfig?.map(termConfig => termConfig.term),
		[termStudyLoadConfig],
	);

	const requisiteWarningsByCourseId = useMemo(
		() => generatedPlanFeedback
			? indexByPropertyArray(
				generatedPlanFeedback.requisiteWarnings,
				warning => warning.courseId,
			) : {},
		[generatedPlanFeedback],
	);

	const startingYear = useMemo(() => {
		if (!currentPlan) {
			return undefined;
		}

		const programVersionCommencingDate = new Date(`1-1-${currentPlan.studyPlan.programVersion.commencingYear}`);

		const latestDate = programVersionCommencingDate > currentPlan.studyPlan.commencement
			? programVersionCommencingDate
			: currentPlan.studyPlan.commencement;

		return (new Date(latestDate)).getFullYear();
	}, [currentPlan]);

	const [courses, completedCoursesById] = useMemo(
		() => [
			indexByPropertyMutate(
				currentPlan?.plannedCourses || [],
				plannedCourse => {
					if (!plannedCourse.courseEntity.id) {
						throw new Error('Course ID not found');
					}
					return plannedCourse.courseEntity.id;
				},
				plannedCourse => new CourseEntity(plannedCourse.courseEntity),
			),
			indexByPropertyMutate(
				currentPlan?.completedCourses || [],
				completedCourse => {
					if (!completedCourse.courseEntity.id) {
						throw new Error('Course ID not found');
					}
					return completedCourse.courseEntity.id;
				},
				completedCourse => new CourseEntity(completedCourse.courseEntity),
			),
		],
		[currentPlan],
	);

	const termsGroupedByYear = useMemo(() => {
		if (!currentPlan) {
			return {};
		}

		const termsAscending = [...currentPlan.terms]
			.sort((x, y) => {
				// combine year, period and term into single value to be used for comparison
				const xStudyPeriod = (x.year * 100) + (x.periodOrder * 10) + (x.termOrder);
				const yStudyPeriod = (y.year * 100) + (y.periodOrder * 10) + (y.termOrder);

				return xStudyPeriod - yStudyPeriod;
			});

		const termsbyYear = indexByPropertyArray(
			termsAscending,
			'year',
		);

		return termsbyYear;
	}, [currentPlan]);

	const [defaultStudyLoad, defaultLoadsHash] = useMemo<[StudyLoadConfig[number], [string, string]]>(
		() => {
			const defaultLoad = [
				generatePeriodDefaultLoad(0, termStudyLoadConfig),
				generatePeriodDefaultLoad(1, termStudyLoadConfig),
			];

			return [defaultLoad, [JSON.stringify(defaultLoad[0]), JSON.stringify(defaultLoad[1])]];
		},
		[termStudyLoadConfig],
	);

	const generateStudyPlan = useCallback(
		async (abortSignals: AbortSignals, planId: string, requestStudyLoad: StudyLoadConfig) => {
			return generatePlan(abortSignals, planId, requestStudyLoad)
				.then(res => {
					// because this callback is used in multiple places
					// we need to handle possibility of component
					// being unmounted manually
					if (!mounted.current) {
						return;
					}

					setCurrentPlan({
						...res.data,
						studyPlan: new StudyPlanEntity(res.data.studyPlan),
					});

					setCurrentStudyLoad(res.data.studyLoad);
				})
				.catch(handlePlanGenerationError);
		},
		[mounted],
	);

	useOnMount((isMounted, abortSignals) => {
		if (!currentPlan && studyPlanId && termStudyLoadConfig) {
			generateStudyPlan(abortSignals, studyPlanId, []);
		}
	}, [currentPlan, studyPlanId, termStudyLoadConfig]);

	const generateStudyPlanDebounced = useDebounce(
		generateStudyPlan,
		[setCurrentPlan, mounted],
		400,
	);

	const resetPlanStudyLoad = () => {
		if (!currentPlan || !studyPlanId) {
			console.error('Cannot reset study load');
			return;
		}
		const newStudyLoadConfig: StudyLoadConfig = [];
		const yearsWithGeneratedTerms = currentPlan.terms
			.map(term => term.year);
		const maxYear = Math.max(...yearsWithGeneratedTerms);

		populateStudyLoad(newStudyLoadConfig, maxYear, termStudyLoadConfig);
		generateStudyPlanDebounced(studyPlanId, newStudyLoadConfig);
	};

	const getLoadYearIndexFromYear = (year: number) => year - (startingYear || 0);

	const togglePeriodStudyLoad = (
		planId: string,
		year: number,
		period: number,
		shouldResetLoad: boolean,
	) => {
		if (!defaultStudyLoad || !studyPlanId) {
			console.error('Cannot toggle period study load');
			return;
		}
		const yearIndex = year - (startingYear || 0);
		const modifiedConfig = [...currentStudyLoad];

		if (shouldResetLoad) { // reset the load
			modifiedConfig[yearIndex][period] = structuredClone(defaultStudyLoad[period]);
		} else { // clear the load
			modifiedConfig[yearIndex][period] = structuredClone(defaultStudyLoad[period])
				.map(() => 0);
		}

		setCurrentStudyLoad(modifiedConfig);
		generateStudyPlanDebounced(studyPlanId, modifiedConfig);
	};

	const setTermStudyLoad = (
		yearIndex: number,
		period: number,
		term: number,
		newValue: number,
	) => {
		if (!studyPlanId) {
			console.error('Cannot set term load - missing study plan id');
			return;
		}
		const modifiedConfig = [...currentStudyLoad];

		modifiedConfig[yearIndex][period][term] = newValue;

		setCurrentStudyLoad(modifiedConfig);
		generateStudyPlanDebounced(studyPlanId, modifiedConfig);
	};

	const getProgramTermConfig = (period: YearHalf, term: TermIndex) => {
		if (!termStudyLoadConfig) {
			throw new Error('Term load config not yet retrieved');
		}

		const termConfig = termStudyLoadConfig
			.find(currentConfig => (
				currentConfig.term.studyPeriod.order === period
				&& currentConfig.term.order === term
			));

		if (!termConfig) {
			throw new Error('Term load config not found');
		}

		return termConfig;
	};

	/**
	 * Incremement or decrement the study load for a specific term
	 * in accordance to the program configuration for that term
	 */
	const adjustTermStudyLoad = (
		action: 'add' | 'subtract',
		year: number,
		periodIndex: number,
		termIndex: number,
	) => {
		const yearIndex = getLoadYearIndexFromYear(year);

		const existingCustomLoad = currentStudyLoad?.[yearIndex]?.[periodIndex]?.[termIndex];
		const {
			increment, defaultLoad, maximum, minimum,
		} = getProgramTermConfig(periodIndex, termIndex);

		// use a local custom load if set;
		// if not, fallback to the configured default
		const existingTermLoad = existingCustomLoad ?? defaultLoad;

		const modifiedValue = action === 'add'
			? safeAdd(existingTermLoad, increment)
			: safeSubtract(existingTermLoad, increment);

		// clamp to min and max, then update
		const newLoadValue = clamp(modifiedValue, minimum, maximum);
		setTermStudyLoad(yearIndex, periodIndex, termIndex, newLoadValue);
	};

	const getCurrentPeriodStudyLoad = (year: number, periodIndex: number): number[] | undefined => {
		const yearIndex = getLoadYearIndexFromYear(year);
		return currentStudyLoad[yearIndex]?.[periodIndex] ?? undefined;
	};

	const getCurrentTermStudyLoad = (year: number, periodIndex: number, termIndex: number) => {
		const periodLoad = getCurrentPeriodStudyLoad(year, periodIndex);
		return periodLoad?.[termIndex];
	};

	const isStudyLoadSetToDefaultValues = useMemo(
		() => {
			const areAllPeriodsDefaultValues = currentStudyLoad
				.every(yearLoad => (
					yearLoad.every((periodLoad, index) => (
						JSON.stringify(periodLoad) === defaultLoadsHash[index]
					))
				));

			return (
				// if study load config is in it's default state
				currentStudyLoad.length === 0
				// if study load is configured identically to the default load
				|| areAllPeriodsDefaultValues
			);
		},
		[currentStudyLoad, defaultLoadsHash],
	);

	const isInitialDataLoading = !currentPlan || !termStudyLoadConfig;

	return {
		currentPlan,
		courses,
		allTerms,
		allStudyPeriods,
		completedCoursesById,
		resetPlanStudyLoad,
		togglePeriodStudyLoad,
		adjustTermStudyLoad,
		requisiteWarningsByCourseId,
		isStudyLoadSetToDefaultValues,
		getProgramTermConfig,
		isInitialDataLoading,
		termsGroupedByYear,
		getCurrentPeriodStudyLoad,
		getCurrentTermStudyLoad,
		startingYear,
	};
};

export default useStudyPlan;
